import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LearningMaterials from "@components/LearningMaterials";
import styles from "./styles.module.scss";
import { ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import ModalBox from "@components/ui-kit/ModalBox";
import { ActiveStepStore } from "src/store/onboarding.store";
import "react-datepicker/dist/react-datepicker.css";
import { useSectionStore } from "src/store/croModule.state";
import { useMarketStore } from "src/store/market.state";
import { COGSForm } from "@components/DataEntry/components/SearchCOGS";
import { steps } from "./utils";

interface FlowProgressLineProps {
  activeStep: number;
  setActiveStep: (value: number) => void;
}

const FlowProgressLine: React.FC<FlowProgressLineProps> = ({
  activeStep,
  setActiveStep,
}) => {
  const [modal, setModal] = useState(false);
  const nav = useNavigate();

  const { setStep, subStepsCompleted, markSubStepComplete } = ActiveStepStore();
  const { isWhatIf, changeWhatIf } = useSectionStore();
  const { setIsWhatIf } = useMarketStore();

  const shouldShowLearning = [0, 1, 2, 3, 4].includes(activeStep);

  const handleZeroStep = () => {
    setStep(1);
  };

  const handleFirstStep = () => {
    nav("/connect-seller-account");
  };

  const handleThree = () => {
    if (!subStepsCompleted[3][0]) {
      markSubStepComplete(3, 0);
    } else {
      changeWhatIf(!isWhatIf);
      setIsWhatIf(!isWhatIf);
      markSubStepComplete(3, 1);
      setStep(4);
    }
  };

  const getStepIcon = (stepNum: number) => {
    switch (stepNum) {
      case 0:
        return (
          <button onClick={handleZeroStep} className={styles.linkButton}>
            <ArrowUpRight size={16} color="#5295E0" className={styles.icon} />
          </button>
        );
      case 1:
        return (
          <button onClick={handleFirstStep} className={styles.linkButton}>
            <ArrowUpRight size={16} color="#5295E0" className={styles.icon} />
          </button>
        );
      case 2:
        return (
          <>
            <button onClick={() => setModal(true)}>
              <Plus size={12} color="#5295E0" className={styles.icon} />
            </button>
            {modal && (
              <ModalBox
                className={styles.modal}
                title="Add COGS Data Entry"
                isOpen={modal}
                onClose={() => setModal(false)}
              >
                <COGSForm
                  className={styles.cogsForm}
                  onSubmit={() => {
                    setActiveStep(3);
                    setModal(false);
                    nav("/market-intelligence");
                  }}
                />
              </ModalBox>
            )}
          </>
        );
      case 3:
        return (
          <button onClick={handleThree}>
            <ArrowRight size={12} color="#5295E0" className={styles.icon} />
          </button>
        );
      case 4:
        return (
          <button
            onClick={() => {
              nav("/canvas?disabepreset=true");
              setStep(5);
            }}
          >
            <ArrowRight size={12} color="#5295E0" className={styles.icon} />
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.timeline}>
        {steps.map((step) => {
          const idKey = String(step.id);

          // Dot handling
          if (step.type === "dot") {
            const dotMap: Record<string, boolean> = {
              "dot-3a": subStepsCompleted[3][0],
              "dot-3b": subStepsCompleted[3][1],
            };
            const dotCompleted = dotMap[idKey] || false;
            return (
              <div key={idKey} className={styles.dotWrapper}>
                <div
                  className={`${styles.dot} ${dotCompleted ? styles.completed : ""}`}
                />
              </div>
            );
          }

          const stepNum = Number(step.id);

          // Count completed parts per step
          const partCountMap: Record<
            number,
            { total: number; completed: number }
          > = {
            3: {
              total: 2,
              completed: subStepsCompleted[3].filter(Boolean).length,
            },
          };
          const { total: totalParts = 0, completed: completedParts = 0 } =
            partCountMap[stepNum] || {};

          const subCountLabel =
            step.sub && totalParts > 0 ? `${completedParts}/${totalParts}` : "";

          const isActive = stepNum === activeStep;
          const isDone = stepNum < activeStep;

          return (
            <div key={stepNum} className={styles.step}>
              <div
                className={`${styles.circle} ${isActive ? styles.active : ""} ${isDone ? styles.completed : ""}`}
              >
                {stepNum + 1}
              </div>
              <div className={styles.label}>
                <span
                  className={`${isActive ? styles.textActive : ""} ${isDone ? styles.textCompleted : ""}`}
                >
                  {step.title} {subCountLabel}
                </span>
                {step.sub && isActive && (
                  <span className={styles.sub}>{step.sub}</span>
                )}
                {step.link && isActive && getStepIcon(stepNum)}
              </div>
            </div>
          );
        })}
      </div>

      {shouldShowLearning && (
        <LearningMaterials setModal={setModal} activeStep={activeStep} />
      )}
    </div>
  );
};

export default FlowProgressLine;
